/*
 * Session state, driven by /api/me and the BFF cookie.
 *
 * - `sessionSignal` is the single source of truth for "who is logged in".
 * - `bootstrapSession()` runs once at app startup. On 401 we redirect to the
 *   backend login endpoint (Keycloak challenge), which comes back through
 *   /signin-oidc onto the same origin.
 * - `signOut()` navigates to /api/auth/logout — the browser follows the
 *   RP-initiated logout chain (server -> Keycloak end_session ->
 *   /signout-callback-oidc -> /) end-to-end. Doing this via fetch leaves
 *   the Keycloak SSO session alive because cross-origin redirect handling
 *   in XHR doesn't propagate the necessary cookies.
 */

import { signal } from '@preact/signals';
import { api, ApiError } from '../api/client';
import type { MeResponse } from '../api/types';

export interface SessionState {
  user: MeResponse | null;
  loading: boolean;
  /** Set once bootstrap ran (regardless of outcome). Guards can pause on `!ready`. */
  ready: boolean;
}

export const sessionSignal = signal<SessionState>({ user: null, loading: true, ready: false });

/** Die Web-HMI-Sitzung hat ihre harte Grenze erreicht (Server: 401 mit
 *  reason webhmi_session_expired). Dann NICHT still neu anmelden, das ginge
 *  ueber die noch lebende Keycloak-Sitzung ohne Kennwort und hebelte die
 *  Grenze aus; die Web-HMI zeigt einen Hinweis mit "Neu anmelden". */
export const webHmiExpiredSignal = signal(false);

let bootstrapPromise: Promise<void> | null = null;

/** Fires exactly once per page-life: subsequent 401s (parallel API requests,
 *  hub reconnect, whatever) that call redirectToLogin() collapse into the same
 *  navigation instead of racing. Reset on hot-reload only, which is fine
 *  because HMR replaces the module fully. */
let redirectFired = false;

/**
 * Central "session is gone, kick the user back to Keycloak" handler. Called
 * from:
 *   - The API-client's 401 branch (a live request found the cookie expired)
 *   - bootstrapSession's initial /api/me probe
 *   - Anywhere else that concludes the current identity is dead
 *
 * Guarded against multi-fire so a burst of parallel-failing requests doesn't
 * spam the login endpoint. The redirect chain is BFF login → Keycloak challenge
 * → /signin-oidc → landed on returnTo. If Keycloak still has a valid SSO
 * session the user barely notices; otherwise Keycloak shows its login form.
 */
export function redirectToLogin(returnTo?: string): void {
  if (redirectFired) return;
  redirectFired = true;
  // Flip the session signal to a "logging you out" state so any component
  // still rendering during the brief window before navigation shows a
  // consistent view instead of stale data.
  sessionSignal.value = { user: null, loading: true, ready: false };
  const target = returnTo
    ?? (window.location.origin + window.location.pathname + window.location.search);
  window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(target)}`;
}

export function bootstrapSession(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    try {
      const me = await api.get<MeResponse>('/api/me');
      sessionSignal.value = { user: me, loading: false, ready: true };
    } catch (err) {
      // 401 is handled by the API-client wrapper (it already fired
      // redirectToLogin before rethrowing). We just need to swallow the
      // resulting ApiError and hold the UI in "loading" so the browser
      // navigation isn't briefly replaced by a flash of the API-error page.
      // Web-HMI abgelaufen: nicht auf "laden" haengen bleiben, die Oberflaeche
      // zeigt den Hinweis mit "Neu anmelden".
      if (err instanceof ApiError && err.status === 401 && webHmiExpiredSignal.value) {
        sessionSignal.value = { user: null, loading: false, ready: true };
        return;
      }
      if (err instanceof ApiError && err.status === 401) return;
      // Any other failure — surface it so the AuthGuard renders an
      // API-unreachable state instead of a blank screen.
      // eslint-disable-next-line no-console
      console.error('Session bootstrap failed', err);
      sessionSignal.value = { user: null, loading: false, ready: true };
    }
  })();
  return bootstrapPromise;
}

export function signOut(): void {
  // Full browser navigation, not fetch — the redirect chain
  // /api/auth/logout -> Keycloak end_session -> /signout-callback-oidc -> /
  // must traverse at the browser level. A fetch()-driven POST gets caught
  // in cross-origin redirect handling and leaves the Keycloak SSO session
  // alive, so the next /api/me would silently re-authenticate.
  window.location.href = '/api/auth/logout';
}
